import React from 'react';
import {GridColDef, GridRenderCellParams, GridValueGetter} from '@mui/x-data-grid-pro';
import {Delete, OpenInNewOutlined} from '@mui/icons-material';
import {Button} from '@mui/material';
import {useLazyQuery, useMutation} from '@apollo/client';
import {useSnackbar} from 'notistack';
import {OVBuilders_ovrecommends} from '../../../schema-types/OVBuilders';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {themeColors} from '../../../theme/Theme';
import {BUILDER_TYPES, serverToBuilderTypeLabel} from '../constants/SolutionType';
import {URGENCY} from '../../../constants/Urgency';
import {OpenInCuro} from '../../../utils/getCuroURL';
import {RenderColor} from '../../../components/EditorGrid/GridRenderComponents';

import {useAccountContext} from '../../../contexts/AccountContext';
import {
  deleteSelectedBuilderFactory,
  onCompletedDeleteBuilderFactory,
  updateDeleteRecommendation,
} from './BuilderGridHelpers';
import {DELETE_OV_BUILDER, OV_BUILDER_ASSOCIATED_ITEMS} from './builderOvPotQueries';
import {
  OvbuilderAssociatedItems,
  OvbuilderAssociatedItemsVariables,
} from '../../../schema-types/OvbuilderAssociatedItems';
import {useReviewContext} from '../../../contexts/ReviewContext';
import {
  DeleteOvbuilder,
  DeleteOvbuilderVariables,
} from '../../../schema-types/DeleteOvbuilder';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {useReviewOVBuilds as useReviewOVBuilders} from '../hooks/useOVBuilds';
import {renderCellDateTime} from '../../../utils/renderCellDateTime';

export const getBuilderStatus = (recc: OVRecommendation) => {
  if (recc.recommends?.some((x) => x.isFailedSubmission)) {
    return 'Error';
  }
  return recc.recommends && recc.recommends.length > 0 && recc.recommends[0]?.curoId
    ? 'Submitted'
    : 'Draft';
};
export const valueGetterStatus: GridValueGetter<OVRecommendation> = (params, row) => {
  return getBuilderStatus(row);
};

export const valueGetterFinalised: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.isFinalised ? 'Finalised' : 'Not finalised';
};

export const valueGetterAdvice: GridValueGetter<OVRecommendation> = (params, row) => {
  const label = serverToBuilderTypeLabel(row.solutionType);
  return label;
};

export const valueGetterName: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.name || 'No name given';
};

export const valueGetterGoal: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.goal?.name;
};

export const valueGetterUrgency: GridValueGetter<OVRecommendation> = (params, row) => {
  return row.urgency;
};

export function builderGridRowActions(params: GridRenderCellParams<OVRecommendation>) {
  const {row} = params;
  return (
    <>
      {getBuilderStatus(row) === 'Submitted' ? null : (
        <BuilderActions builderId={row.id as string} />
      )}
      <Button
        className="open-recc"
        variant="text"
        onClick={() => {
          const path = row.isDentist ? './builder' : './recommends-builder';
          window.open(`${path}?ovrecommendId=${row.id}`, '_blank');
        }}
      >
        <OpenInNewOutlined />
      </Button>
      <ShowIfDebugWrapper>
        {row.recommends?.length
          ? row.recommends.map((r, index) => {
              return (
                <OpenInCuro key={r.curoId} curoId={r.curoId} curoType="recommendation">
                  {index}
                </OpenInCuro>
              );
            })
          : null}
      </ShowIfDebugWrapper>
    </>
  );
}

type BuilderColumnType = GridColDef<OVBuilders_ovrecommends> & {
  field: keyof OVBuilders_ovrecommends;
};

export const ehonsBuilderColumns: BuilderColumnType[] = [
  {
    field: 'id',
    headerName: 'Status',
    flex: 0.7,
    headerAlign: 'center',

    renderCell: RenderColor({
      Submitted: themeColors.lightGreen,
      Draft: themeColors.greyLight,
      Failed: themeColors.red,
    }),
    valueGetter: valueGetterStatus,
  },
  {
    field: 'isFinalised',
    headerName: 'Finalised',
    flex: 0.7,
    headerAlign: 'center',

    renderCell: RenderColor({
      Finalised: themeColors.lightGreen,
      'Not finalised': themeColors.greyLight,
    }),
    valueGetter: valueGetterFinalised,
  },
  {
    field: 'solutionType',
    headerName: 'Advice',
    flex: 1,
    headerAlign: 'center',

    renderCell: RenderColor({
      [BUILDER_TYPES.Recommendation]: themeColors.lightYellow,
      [BUILDER_TYPES.Instruction]: themeColors.lightBlue,
    }),
    valueGetter: valueGetterAdvice,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    headerAlign: 'center',

    valueGetter: valueGetterName,
  },
  {
    field: 'selectedMandate',
    headerName: 'Mandate',
    flex: 2,
    headerAlign: 'center',
    valueGetter: (params, row) => {
      return row.selectedMandate;
    },
  },
  // {
  //   field: 'goal',
  //   headerName: 'Linked Goal',
  //   flex: 2,
  //   headerAlign: 'center',

  //   valueGetter: valueGetterGoal,
  // },
  {
    field: 'urgency',
    headerName: 'Urgency',
    flex: 1,
    headerAlign: 'center',

    valueGetter: valueGetterUrgency,
    renderCell: RenderColor({
      [URGENCY.NORMAL_ID]: themeColors.greyLight,
      [URGENCY.TAX_ID]: themeColors.lightGreen,
      [URGENCY.URGENT_ID]: themeColors.lightAlert,
      default: themeColors.greyLight,
    }),
  },
  {
    field: 'dateCreated',
    headerName: 'Date Created',
    flex: 1,
    headerAlign: 'center',
    renderCell: renderCellDateTime,
    valueGetter: (value, row, column, apiRef) => {
      return row?.dateCreated?.valueOf(); // used for ordering
    },
  },
  {
    field: 'dateUpdated',
    headerName: 'Last Updated',
    flex: 1,

    headerAlign: 'center',
    renderCell: renderCellDateTime,
    valueGetter: (value, row, column, apiRef) => {
      return row.dateUpdated?.valueOf(); // used for ordering
    },
  },
  {
    field: '__typename',
    headerName: ' ',
    flex: 1,
    headerAlign: 'center',

    renderCell: builderGridRowActions,
  },
];

export const BuilderActions: React.FC<{builderId: string}> = ({builderId}) => {
  const {accountId} = useAccountContext();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {reviewId} = useReviewContext();
  const {data} = useReviewOVBuilders();
  const [getAssociatedReccs, {data: associatedReccsData, error: associatedReccsError}] =
    useLazyQuery<OvbuilderAssociatedItems, OvbuilderAssociatedItemsVariables>(
      OV_BUILDER_ASSOCIATED_ITEMS
    );
  const onCompletedDeleteRecommendation =
    onCompletedDeleteBuilderFactory(enqueueSnackbar);
  const [deleteRecommendation] = useMutation<DeleteOvbuilder, DeleteOvbuilderVariables>(
    DELETE_OV_BUILDER,
    {
      onCompleted: onCompletedDeleteRecommendation,
      onError: (err) => {
        enqueueSnackbar(`Unable to delete the recommendation. ${err.message}`, {
          variant: 'error',
          autoHideDuration: null,
        });
      },
      update: updateDeleteRecommendation,
    }
  );
  const recommends =
    data?.ovrecommends?.filter((r) => {
      if (!r?.review?.id) {
        return false;
      }
      const inThisReview = r.review.id === reviewId;
      return inThisReview;
    }) || [];

  const deleteSelectedBuilder = deleteSelectedBuilderFactory({
    recommends,
    enqueueSnackbar,
    getAssociatedReccs,
    closeSnackbar,
    accountId,
    reviewId,
    deleteRecommendation,
  });
  return (
    <Button
      data-test="Delete recc"
      variant="text"
      onClick={() => deleteSelectedBuilder(builderId)}
    >
      <Delete />
    </Button>
  );
};
